// src/components/AdminLayout.jsx
import React, { useContext, useState } from 'react';
import { 
  Box, 
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Code as CodeIcon,
  Notifications as NotificationIcon,
  PersonAdd as PersonAddIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Speed as SpeedIcon,
  Circle as CircleIcon,
  AccountTree as StrategyIcon,
  Send as SendIcon,
  ExpandLess,
  ExpandMore,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';

const DRAWER_WIDTH = 260;

const AdminLayout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(true);
  const [strategyOpen, setStrategyOpen] = useState(true);

  const mainMenuItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard', 
      icon: <DashboardIcon sx={{ fontSize: 20 }} /> 
    },
    { 
      label: 'Scripts', 
      path: '/admin/scripts', 
      icon: <CodeIcon sx={{ fontSize: 20 }} /> 
    },
    { 
      label: 'Strategies', 
      path: '/admin/strategies', 
      icon: <StrategyIcon sx={{ fontSize: 20 }} /> 
    },
  ];

  // Alert submenu items
  const alertMenuItems = [
    { 
      label: 'All Alerts', 
      path: '/admin/alerts', 
      icon: <VisibilityIcon sx={{ fontSize: 18 }} /> 
    },
    { 
      label: 'Create Alert', 
      path: '/admin/alerts/create', 
      icon: <AddIcon sx={{ fontSize: 18 }} /> 
    },
    { 
      label: 'Live Monitor', 
      path: '/admin/alerts/monitor', 
      icon: <SpeedIcon sx={{ fontSize: 18 }} /> 
    },
    { 
      label: 'Test Sender', 
      path: '/admin/alerts/test-sender', 
      icon: <SendIcon sx={{ fontSize: 18 }} /> 
    },
  ];

  // Strategy Builder submenu items
  const strategyBuilderMenuItems = [
    { 
      label: 'All Strategies', 
      path: '/admin/strategy-builder', 
      icon: <VisibilityIcon sx={{ fontSize: 18 }} /> 
    },
    { 
      label: 'Create Strategy', 
      path: '/admin/strategy-builder/create', 
      icon: <AddIcon sx={{ fontSize: 18 }} /> 
    },
  ];

  if (user?.role === 'admin') {
    mainMenuItems.push({ 
      label: 'Add Officer', 
      path: '/officer-signup', 
      icon: <PersonAddIcon sx={{ fontSize: 20 }} />,
    });
  }

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isPathActive = (path) => {
    if (path === '/admin/alerts') {
      return location.pathname === path;
    }
    if (path === '/admin/strategy-builder') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isAlertSectionActive = alertMenuItems.some(item => isPathActive(item.path));
  const isStrategySectionActive = strategyBuilderMenuItems.some(item => isPathActive(item.path));

  const drawer = (
    <Box sx={{ 
      height: '100%',
      background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1729 100%)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
      }
    }}>
      {/* Logo Header - Compact */}
      <Box sx={{ 
        p: 2.5,
        pb: 2,
        position: 'relative',
        zIndex: 1,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
          }}>
            <Typography sx={{ 
              color: '#fff', 
              fontWeight: 800, 
              fontSize: '1.1rem',
              letterSpacing: '-0.5px',
            }}>
              T
            </Typography>
          </Box>
          <Box>
            <Typography 
              sx={{ 
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.05rem',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
              }}
            >
              TradeWave
            </Typography>
            <Typography 
              sx={{ 
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.65rem',
                fontWeight: 500,
              }}
            >
              v2.1.0
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* User Profile - Compact */}
      <Box sx={{ px: 2, pb: 2, position: 'relative', zIndex: 1 }}>
        <Box sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          borderRadius: 2,
          p: 1.5,
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                fontWeight: 700,
                fontSize: '0.95rem',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.4)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'A'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                sx={{ 
                  color: '#fff', 
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mb: 0.3,
                  lineHeight: 1.2,
                }}
              >
                {user?.full_name || user?.username}
              </Typography>
              <Chip
                label={user?.role?.toUpperCase() || 'USER'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.3px',
                  background: 'rgba(99, 102, 241, 0.2)',
                  color: '#a5b4fc',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  '& .MuiChip-label': { px: 1.2, py: 0 }
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* Navigation Menu - Compact */}
      <List sx={{ flex: 1, px: 1.5, py: 0, position: 'relative', zIndex: 1 }}>
        {/* Main Menu Items */}
        {mainMenuItems.map((item) => {
          const isActive = isPathActive(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.3 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 1.5,
                  py: 1,
                  px: 2,
                  minHeight: 40,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  ...(isActive ? {
                    background: 'rgba(99, 102, 241, 0.15)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3,
                      height: 20,
                      background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)',
                      borderRadius: '0 4px 4px 0',
                      boxShadow: '0 0 8px rgba(99, 102, 241, 0.6)',
                    },
                    '&:hover': {
                      background: 'rgba(99, 102, 241, 0.2)',
                    }
                  } : {
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.05)',
                    }
                  }),
                }}
              >
                <ListItemIcon sx={{ 
                  color: isActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.5)',
                  minWidth: 36,
                  transition: 'all 0.2s',
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.85rem',
                    letterSpacing: '-0.1px',
                  }}
                />
                {isActive && (
                  <CircleIcon sx={{ 
                    fontSize: 5, 
                    color: '#6366f1',
                    filter: 'drop-shadow(0 0 3px rgba(99, 102, 241, 0.8))',
                  }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}

        {/* Alerts Section with Submenu */}
        <ListItem disablePadding sx={{ mb: 0.3 }}>
          <ListItemButton
            onClick={() => setAlertsOpen(!alertsOpen)}
            sx={{
              borderRadius: 1.5,
              py: 1,
              px: 2,
              minHeight: 40,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              ...(isAlertSectionActive ? {
                background: 'rgba(99, 102, 241, 0.15)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
              } : {
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.05)',
                }
              }),
            }}
          >
            <ListItemIcon sx={{ 
              color: isAlertSectionActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.5)',
              minWidth: 36,
            }}>
              <NotificationIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText 
              primary="Alerts"
              primaryTypographyProps={{
                fontWeight: isAlertSectionActive ? 600 : 500,
                color: isAlertSectionActive ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.85rem',
              }}
            />
            {alertsOpen ? 
              <ExpandLess sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 18 }} /> : 
              <ExpandMore sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 18 }} />
            }
          </ListItemButton>
        </ListItem>

        {/* Alerts Submenu */}
        <Collapse in={alertsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {alertMenuItems.map((item) => {
              const isActive = isPathActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.2 }}>
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      py: 0.75,
                      pl: 5,
                      pr: 2,
                      minHeight: 36,
                      transition: 'all 0.2s',
                      ...(isActive ? {
                        background: 'rgba(99, 102, 241, 0.1)',
                        '&:hover': {
                          background: 'rgba(99, 102, 241, 0.15)',
                        }
                      } : {
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.03)',
                        }
                      }),
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: isActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.4)',
                      minWidth: 32,
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.8rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>

        {/* Strategy Builder Section with Submenu */}
        <ListItem disablePadding sx={{ mb: 0.3 }}>
          <ListItemButton
            onClick={() => setStrategyOpen(!strategyOpen)}
            sx={{
              borderRadius: 1.5,
              py: 1,
              px: 2,
              minHeight: 40,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              ...(isStrategySectionActive ? {
                background: 'rgba(99, 102, 241, 0.15)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
              } : {
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.05)',
                }
              }),
            }}
          >
            <ListItemIcon sx={{ 
              color: isStrategySectionActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.5)',
              minWidth: 36,
            }}>
              <StrategyIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText 
              primary="Strategy Builder"
              primaryTypographyProps={{
                fontWeight: isStrategySectionActive ? 600 : 500,
                color: isStrategySectionActive ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.85rem',
              }}
            />
            {strategyOpen ? 
              <ExpandLess sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 18 }} /> : 
              <ExpandMore sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 18 }} />
            }
          </ListItemButton>
        </ListItem>

        {/* Strategy Builder Submenu */}
        <Collapse in={strategyOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {strategyBuilderMenuItems.map((item) => {
              const isActive = isPathActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.2 }}>
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      py: 0.75,
                      pl: 5,
                      pr: 2,
                      minHeight: 36,
                      transition: 'all 0.2s',
                      ...(isActive ? {
                        background: 'rgba(99, 102, 241, 0.1)',
                        '&:hover': {
                          background: 'rgba(99, 102, 241, 0.15)',
                        }
                      } : {
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.03)',
                        }
                      }),
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: isActive ? '#a5b4fc' : 'rgba(255, 255, 255, 0.4)',
                      minWidth: 32,
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.8rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </List>

      {/* Status Bar - Compact */}
      <Box sx={{ 
        p: 2, 
        pt: 1.5,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative',
        zIndex: 1,
      }}>
        <Stack spacing={1.5}>
          {/* Status Indicator */}
          <Box sx={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 1.5,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 8px rgba(16, 185, 129, 0.6)',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }} />
              <Typography sx={{ 
                color: '#10b981', 
                fontSize: '0.75rem', 
                fontWeight: 600,
              }}>
                System Active
              </Typography>
            </Stack>
            <Typography sx={{ 
              color: 'rgba(16, 185, 129, 0.7)', 
              fontSize: '0.7rem', 
              fontWeight: 500,
            }}>
              Live
            </Typography>
          </Box>

          {/* Logout Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
            onClick={handleLogout}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              py: 1,
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '-0.1px',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'rgba(239, 68, 68, 0.5)',
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#f87171',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
              },
            }}
          >
            Logout
          </Button>
        </Stack>
      </Box>

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Mobile Header */}
      {isMobile && (
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2,
                color: '#1e293b',
                '&:hover': { background: '#f1f5f9' }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>
                  T
                </Typography>
              </Box>
              <Typography sx={{
                fontWeight: 700,
                fontSize: '1.1rem',
                color: '#1e293b',
                letterSpacing: '-0.3px',
              }}>
                TradeWave
              </Typography>
            </Stack>
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { 
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': { 
              width: DRAWER_WIDTH,
              border: 'none',
              boxShadow: '4px 0 24px rgba(0, 0, 0, 0.08)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: '#f8fafc',
          pt: { xs: 7, sm: 8, lg: 0 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
